/**
 * What Mappings → Clients and Mappings → Techs share (ADRs 0078 and 0082; the
 * owner, 2026-10-19: "the techs need the drop down like clients"): a compact
 * row for each, a status chip per service, that opens underneath to see and
 * change everything. Anywhere on a row opens or closes it, one at a time; its
 * arrow is the keyboard's way in. The components are SummaryRow.tsx's.
 */
import { type MouseEvent, useEffect, useRef } from "react";

/** A service's status for one row, as its narrow column shows it. */
export type Status = { label: string; tone: "done" | "todo" | "none" };

export const NOT_MAPPED: Status = { label: "None", tone: "none" };

export const STATUS_CLASS: Record<Status["tone"], string> = {
  done: "bg-status-logged-bg text-status-logged-fg",
  todo: "bg-status-needs-client-bg text-status-needs-client-fg",
  none: "bg-panel-2 text-muted",
};

/** A section's heading in an open row. */
export const sectionTitle = "text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted";

/**
 * A summary row: the whole of it is the way in, tinted while it's open.
 * Narrower than a laptop (lg) it's a card, so the table never scrolls
 * sideways: the row's own cells on its first line, then its chips
 * (chipCellClass), each under its service's name. Its table, body and the
 * row that opens under it are blocks there (`max-lg:block`), and the table's
 * heading row is hidden.
 */
export function summaryRowClass(open: boolean): string {
  return (
    "group cursor-pointer border-t border-line align-top transition-colors max-lg:flex max-lg:flex-wrap " +
    (open ? "bg-brand-tint" : "hover:bg-brand-tint/50")
  );
}

/**
 * A chip's cell (SummaryRow.tsx's ChipCell): in a card, after the row's
 * first line. Each page adds the share of a line it takes (`basis-…`).
 */
export const chipCellClass = "py-3 pr-3 max-lg:order-1 max-lg:pt-0 max-lg:pl-2";

/** A row's own controls: a press that starts on one never opens the row. */
const OWN_CONTROLS = "input, select, textarea, label, a";

/** A click on one of the row's own controls does only that. */
export function stayClosed(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

/** The text selected on the page now ("" if none). */
function selectedText(): string {
  const selection = window.getSelection();
  return selection === null || selection.isCollapsed ? "" : selection.toString().trim();
}

/** The row a mouse event is on, by its `attribute`, if any. */
function rowOf(target: EventTarget, attribute: string): string | null {
  if (!(target instanceof Element)) {
    return null;
  }
  return target.closest<HTMLElement>(`tr[${attribute}]`)?.getAttribute(attribute) ?? null;
}

/**
 * The mouse's way into a table body's rows, each marked with `attribute` (its
 * ID): `toggle` opens or closes the row clicked. Spread `onMouseDown` and
 * `onClick` on the `<tbody>`, with `bodyRef` its ref.
 *
 * A click whose row moved under the pointer (a list above it closed as the
 * mouse went down) lands on the table body, or below the table: it belongs to
 * the row the mouse went down on. A drag that selects text toggles nothing. A
 * double click toggles twice, so the row ends as it was; a third click is
 * ignored. A press that starts on a row's own control never opens it.
 */
export function useRowToggle(attribute: string, toggle: (id: string) => void) {
  // The row the mouse last went down on, and how to stop waiting for its
  // click when it lands outside the table body.
  const pressed = useRef<string | null>(null);
  // The text selected when the mouse went down: a click that ends a new
  // selection (a drag) opens nothing, but a click on text already selected does.
  const selectionAtPress = useRef("");
  const disarm = useRef<() => void>(() => undefined);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const toggleRef = useRef(toggle);

  useEffect(() => {
    toggleRef.current = toggle;
  });

  useEffect(() => {
    // Any mouse press starts afresh: a row pressed before is forgotten.
    const forget = () => {
      disarm.current();
      disarm.current = () => undefined;
      pressed.current = null;
    };
    window.addEventListener("mousedown", forget, true);
    return () => {
      window.removeEventListener("mousedown", forget, true);
      forget();
    };
  }, []);

  /** A new selection made by this press (a drag), not one already there. */
  function selecting(): boolean {
    const now = selectedText();
    return now !== "" && now !== selectionAtPress.current;
  }

  function onMouseDown(event: MouseEvent) {
    selectionAtPress.current = selectedText();
    const onControl = event.target instanceof Element && event.target.closest(OWN_CONTROLS) !== null;
    const id = onControl ? null : rowOf(event.target, attribute);
    pressed.current = id;
    if (id === null) {
      return;
    }
    // If the rows move far enough (a list above closes as the mouse goes
    // down), the click lands outside the table body, where onClick never
    // sees it: catch that one click, right after the mouse comes up.
    const onWindowClick = (click: globalThis.MouseEvent) => {
      window.removeEventListener("click", onWindowClick);
      const target = click.target;
      if (target instanceof Node && bodyRef.current?.contains(target)) {
        return;
      }
      if (click.detail < 1 || selecting() || pressed.current !== id) {
        return;
      }
      pressed.current = null;
      toggleRef.current(id);
    };
    const onUp = () => {
      window.removeEventListener("mouseup", onUp, true);
      window.addEventListener("click", onWindowClick);
      // The click follows the mouse coming up at once, or not at all.
      setTimeout(() => {
        window.removeEventListener("click", onWindowClick);
      }, 0);
    };
    window.addEventListener("mouseup", onUp, true);
    disarm.current = () => {
      window.removeEventListener("mouseup", onUp, true);
      window.removeEventListener("click", onWindowClick);
    };
  }

  function onClick(event: MouseEvent) {
    // Only a mouse click (detail 1 or more) falls back to the pressed row: a
    // key pressed in an open row's details is no click on any row.
    const id = rowOf(event.target, attribute) ?? (event.detail >= 1 ? pressed.current : null);
    pressed.current = null;
    // A double click's second click selects a word as the mouse goes down,
    // so it isn't taken for a drag: it undoes the first click's toggle.
    if (id === null || event.detail > 2 || (event.detail !== 2 && selecting())) {
      return;
    }
    toggle(id);
  }

  return { bodyRef, onMouseDown, onClick };
}
